/**
 * 🔐 Permission Helper Utilities
 * RBAC permission checking and access control
 */

import { Permission } from '@/services/adminStaffService';

// Permission matrix structure
export interface UserPermissions {
  role: {
    id: string;
    name: string;
    type: 'system' | 'custom';
    permissions: Permission[];
  };
  customPermissions?: Permission[];
}

/**
 * Check if user has permission for a resource and action
 * Handles both formats:
 * - String format from backend: "corporate:view"
 * - Object format: { resource: "corporate", action: "view" }
 */
export const hasPermission = (
  userPermissions: UserPermissions | null,
  resource: string,
  action: string
): boolean => {
  if (!userPermissions) {
    return false;
  }

  // Helper to check permission (handles both string and object formats)
  const checkPermission = (p: any): boolean => {
    // String format: "corporate:view"
    if (typeof p === 'string') {
      return p === `${resource}:${action}` || p === '*';
    }
    // Object format: { resource: "corporate", action: "view" }
    if (typeof p === 'object' && p !== null) {
      return (p.resource === resource && p.action === action) || p.resource === '*';
    }
    return false;
  };

  // Check role permissions
  const roleHasPermission = userPermissions.role.permissions.some(checkPermission);

  if (roleHasPermission) {
    return true;
  }

  // Check custom permissions (overrides)
  if (userPermissions.customPermissions) {
    const customHasPermission = userPermissions.customPermissions.some(checkPermission);

    if (customHasPermission) {
      return true;
    }
  }

  return false;
};

/**
 * Check if user has ANY of the specified permissions
 */
export const hasAnyPermission = (
  userPermissions: UserPermissions | null,
  checks: Array<{ resource: string; action: string }>
): boolean => {
  return checks.some((check) => hasPermission(userPermissions, check.resource, check.action));
};

/**
 * Check if user has ALL of the specified permissions
 */
export const hasAllPermissions = (
  userPermissions: UserPermissions | null,
  checks: Array<{ resource: string; action: string }>
): boolean => {
  return checks.every((check) => hasPermission(userPermissions, check.resource, check.action));
};

/**
 * Check if user has a specific role
 */
export const hasRole = (
  userPermissions: UserPermissions | null,
  roleName: string
): boolean => {
  if (!userPermissions) return false;
  return userPermissions.role.name.toLowerCase() === roleName.toLowerCase();
};

/**
 * Check if user is super admin (has super_admin role)
 */
export const isSuperAdmin = (userPermissions: UserPermissions | null): boolean => {
  if (!userPermissions) return false;

  const roleName = userPermissions.role.name.toLowerCase();

  // Check for super_admin role or wildcard permissions
  if (roleName === 'super_admin' || roleName === 'super admin') {
    return true;
  }

  // Check if user has wildcard permission (*)
  const hasWildcard = userPermissions.role.permissions.some(
    (p: any) => p === '*' || p.resource === '*' || p.action === '*'
  );

  return hasWildcard;
};

/**
 * Get all permissions for a resource
 */
export const getResourcePermissions = (
  userPermissions: UserPermissions | null,
  resource: string
): string[] => {
  if (!userPermissions) return [];

  const rolePerms = userPermissions.role.permissions
    .filter((p) => p.resource === resource)
    .map((p) => p.action);

  const customPerms = (userPermissions.customPermissions || [])
    .filter((p) => p.resource === resource)
    .map((p) => p.action);

  // Combine and deduplicate
  return [...new Set([...rolePerms, ...customPerms])];
};

/**
 * Common permission checks for routes/features
 */
export const Permissions = {
  // Bookings
  BOOKINGS_VIEW: { resource: 'bookings', action: 'view' },
  BOOKINGS_MODIFY: { resource: 'bookings', action: 'modify' },
  BOOKINGS_CREATE: { resource: 'bookings', action: 'create' },
  BOOKINGS_CANCEL: { resource: 'bookings', action: 'cancel' },

  // Users
  USERS_VIEW: { resource: 'users', action: 'view' },
  USERS_CREATE: { resource: 'users', action: 'create' },
  USERS_MODIFY: { resource: 'users', action: 'modify' },
  USERS_SUSPEND: { resource: 'users', action: 'suspend' },

  // Financial Operations
  PAYMENTS_VIEW: { resource: 'financial_operations', action: 'view_payments' },
  REFUNDS_PROCESS: { resource: 'financial_operations', action: 'process_refunds' },
  FEES_ADJUST: { resource: 'financial_operations', action: 'adjust_fees' },
  REPORTS_VIEW: { resource: 'financial_operations', action: 'financial_reports' },
  REVENUE_VIEW: { resource: 'financial_operations', action: 'view_revenue' },

  // System Settings
  ROLES_MANAGE: { resource: 'system_settings', action: 'manage_roles' },
  LOGS_VIEW: { resource: 'system_settings', action: 'view_logs' },
  RULES_CONFIGURE: { resource: 'system_settings', action: 'configure_rules' },
  SYSTEM_CONFIGURE: { resource: 'system_settings', action: 'system_configuration' },

  // Vehicles
  VEHICLES_VIEW: { resource: 'vehicles', action: 'view' },
  VEHICLES_APPROVE: { resource: 'vehicles', action: 'approve' },
  VEHICLES_MODIFY: { resource: 'vehicles', action: 'modify' },

  // Drivers
  DRIVERS_VIEW: { resource: 'drivers', action: 'view' },
  DRIVERS_APPROVE: { resource: 'drivers', action: 'approve' },
  DRIVERS_MODIFY: { resource: 'drivers', action: 'modify' },

  // Verification
  VERIFICATION_VIEW: { resource: 'verification', action: 'view' },
  VERIFICATION_APPROVE: { resource: 'verification', action: 'approve' },
  VERIFICATION_REJECT: { resource: 'verification', action: 'reject' },

  // Disputes
  DISPUTES_VIEW: { resource: 'disputes', action: 'view' },
  DISPUTES_RESOLVE: { resource: 'disputes', action: 'resolve' },

  // Admin Staff (only for super admins)
  STAFF_VIEW: { resource: 'admin_staff', action: 'view' },
  STAFF_CREATE: { resource: 'admin_staff', action: 'create' },
  STAFF_MODIFY: { resource: 'admin_staff', action: 'modify' },
  STAFF_DELETE: { resource: 'admin_staff', action: 'delete' },

  // Corporate Accounts (backend uses 'corporate' as resource name)
  CORPORATE_ACCOUNTS_VIEW: { resource: 'corporate', action: 'view' },
  CORPORATE_ACCOUNTS_APPROVE: { resource: 'corporate', action: 'approve' },
  CORPORATE_ACCOUNTS_REJECT: { resource: 'corporate', action: 'reject' },
  CORPORATE_ACCOUNTS_MODIFY: { resource: 'corporate', action: 'modify' },
  CORPORATE_ACCOUNTS_SUSPEND: { resource: 'corporate', action: 'suspend' },
  CORPORATE_ACCOUNTS_DELETE: { resource: 'corporate', action: 'delete' },
};

/**
 * Feature flags based on permissions
 */
export const canAccessFeature = (
  userPermissions: UserPermissions | null,
  feature: keyof typeof FeaturePermissions
): boolean => {
  const requiredPermissions = FeaturePermissions[feature];
  return hasAllPermissions(userPermissions, requiredPermissions);
};

export const FeaturePermissions = {
  DASHBOARD: [Permissions.BOOKINGS_VIEW],
  BOOKINGS: [Permissions.BOOKINGS_VIEW],
  VEHICLES: [Permissions.VEHICLES_VIEW],
  DRIVERS: [Permissions.DRIVERS_VIEW],
  USERS: [Permissions.USERS_VIEW],
  VERIFICATION: [Permissions.VERIFICATION_VIEW],
  DISPUTES: [Permissions.DISPUTES_VIEW],
  FINANCES: [Permissions.PAYMENTS_VIEW],
  ANALYTICS: [Permissions.BOOKINGS_VIEW],
  SETTINGS: [Permissions.SYSTEM_CONFIGURE],
  ADMIN_MANAGEMENT: [Permissions.STAFF_VIEW],
} as const;

/**
 * Get readable permission name
 */
export const getPermissionLabel = (resource: string, action: string): string => {
  const labels: Record<string, string> = {
    'bookings:view': 'View Bookings',
    'bookings:modify': 'Modify Bookings',
    'bookings:create': 'Create Bookings',
    'bookings:cancel': 'Cancel Bookings',
    'users:view': 'View Users',
    'users:create': 'Create Users',
    'users:modify': 'Modify Users',
    'users:suspend': 'Suspend Users',
    'financial_operations:view_payments': 'View Payments',
    'financial_operations:process_refunds': 'Process Refunds',
    'financial_operations:adjust_fees': 'Adjust Fees',
    'financial_operations:financial_reports': 'View Financial Reports',
    'system_settings:manage_roles': 'Manage Roles',
    'system_settings:view_logs': 'View Logs',
    'system_settings:configure_rules': 'Configure Rules',
    'system_settings:system_configuration': 'System Configuration',
    'admin_staff:view': 'View Admin Staff',
    'admin_staff:create': 'Create Admin Staff',
    'admin_staff:modify': 'Modify Admin Staff',
    'admin_staff:delete': 'Delete Admin Staff',
  };

  const key = `${resource}:${action}`;
  return labels[key] || `${action} ${resource}`;
};
